// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Typography, Alert, Radio } from '@mui/material';
import { useProfilingData } from '@shared/advisory/client-profile/providers';
import { AppIcon, Label } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import {
  type ChangeEvent,
  useCallback,
  useState,
  type FC,
  useMemo,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';

export const AdvisoryTypeStep: FC = () => {
  const { t } = useTranslation();

  const { data, updateAdvisoryData, updateStepValidity } = useProfilingData();

  const advisoryTypes = useMemo(
    () => [
      {
        id: 'non-independent',
        name: t('activation-step2.advisory-types.non-independent.name', {
          ns: 'components',
        }) as string,
        description: t(
          'activation-step2.advisory-types.non-independent.description',
          {
            ns: 'components',
          },
        ) as string,
        icon: <AppIcon icon="Account" className="size-5" color="success" />,
        disabled: false,
      },
      {
        id: 'discretionary',
        name: t('activation-step2.advisory-types.discretionary.name', {
          ns: 'components',
        }) as string,
        description: t(
          'activation-step2.advisory-types.discretionary.description',
          { ns: 'components' },
        ) as string,
        icon: <AppIcon icon="TrendingUp" className="size-5" color="main" />,
        disabled: false,
      },
      {
        id: 'independent',
        name: t('activation-step2.advisory-types.independent.name', {
          ns: 'components',
        }) as string,
        description: t('activation-step2.advisory-types.independent.description', {
          ns: 'components',
        }) as string,
        icon: <AppIcon icon="Person" className="size-5" color="success" />,
        disabled: true,
      },
    ],
    [t],
  );

  const [selectedValue, setSelectedValue] = useState(data.advisory_type);

  const advisoryType = useMemo(() => {
    return advisoryTypes.find(type => type.id === selectedValue);
  }, [advisoryTypes, selectedValue]);

  /* istanbul ignore next */
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  }, []);

  useEffect(() => {
    updateStepValidity('advisory-type', selectedValue !== '');

    updateAdvisoryData({
      advisory_type: selectedValue,
      advisory_type_label: advisoryType?.name,
      commission_managed_assets: 0,
      commission_id: 'no-commission',
      commission_label: t(
        selectedValue === 'discretionary'
          ? 'activation-step4.commission-options.no-commission.discretionary.name'
          : 'activation-step4.commission-options.no-commission.name',
        {
          ns: 'components',
        },
      ),
      retrocession: selectedValue === 'discretionary' ? 'no' : 'yes',
      retrocession_label:
        selectedValue === 'discretionary'
          ? t('activation-step3.retrocession-types.no.name', {
              ns: 'components',
            })
          : t('activation-step3.retrocession-types.yes.name', {
              ns: 'components',
            }),
      selectedInstitutions: [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  return (
    <form className="flex flex-col gap-y-4" data-testid="advisory-type-step-form">
      <div className="flex flex-col space-y-2">
        <Typography variant="h5" data-testid="title">
          <Translation i18nKey="activation-step2.title" ns="components" />
        </Typography>
      </div>

      <div className="flex flex-col gap-2">
        {Array.from(advisoryTypes, (type, key) => (
          <div
            key={key}
            className="flex items-center space-x-2 rounded-md border border-gray-200 p-2 transition-colors hover:bg-gray-50 aria-disabled:cursor-not-allowed aria-disabled:border-gray-100 aria-disabled:bg-gray-50 aria-disabled:opacity-60"
            aria-disabled={type.disabled}
          >
            <Radio
              id={type.id}
              data-testid={`radio-${type.id}`}
              name="advisoryType"
              checked={selectedValue === type.id}
              onChange={handleChange}
              value={type.id}
              disabled={type.disabled}
              className="aria-disabled:opacity-50"
            />
            <Label
              htmlFor={type.id}
              className="flex flex-1 cursor-pointer flex-col text-gray-700 aria-disabled:cursor-not-allowed aria-disabled:text-gray-400"
              aria-disabled={type.disabled}
              data-testid={`label-${type.id}`}
            >
              <div className="flex items-center gap-x-2">
                <span className="aria-disabled:opacity-50">{type.icon}</span>
                <span>{type.name}</span>
              </div>
              <p className="text-sm text-gray-600 aria-disabled:text-gray-400">
                {type.description}
              </p>
            </Label>
          </div>
        ))}
      </div>

      {advisoryType && (
        <Alert
          severity="info"
          icon={false}
          className="!bg-alert-info-bg text-alert-info-text [&_.MuiAlert-message]:!py-0"
          data-testid="advisory-type-alert"
        >
          <Translation
            i18nKey="activation-step2.selected-type"
            ns="components"
            values={{
              option: advisoryType?.name,
            }}
          />
        </Alert>
      )}
    </form>
  );
};
